import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { SelectField } from '../bulma'
import { Link } from '../core'

/* doc
---
title: ShipToStoreSelector
name: Ship to Store Selector
category: React - Bulma
---
A `ShipToStoreSelector` component created from `SelectField` component and
some additional HTML for displaying address and store information.

### Props
| Prop Name       | Description                                         | Type            | Default Value             | Required?     |
|-----------------|-----------------------------------------------------|-----------------|---------------------------|---------------|
| stores          | Stores that support Ship to Store arranged by state | dictionary      | {}                        | no            |
| namespace       | Namespace for ids                                   | string          | ''                        | no            |
| defaultValue    | Default store id                                    | int             | null                      | no            |
| isVisible       | Dictates whether the field is visible               | bool            | true                      | no            |

```html_example
  <form>
    <ShipToStoreSelector stores={stores} namespace="example" defaultValue={defaultValue} isVisible=true>
    </ShipToStoreSelector>
  </form>
```
*/
class ShipToStoreSelector extends Component {
  constructor(props) {
    super(props)
    const { defaultValue } = this.props

    this.state = {
      activeStore: this.findStore(defaultValue),
    }

    this.findStore = this.findStore.bind(this)
    this.onChangeHandler = this.onChangeHandler.bind(this)
    this.renderChoices = this.renderChoices.bind(this)
  }

  onChangeHandler(name, value) {
    this.setState({
      activeStore: this.findStore(value),
    })
  }

  /**
  * Finds and returns the store based on the given id
  */
  findStore(id) {
    const { stores } = this.props
    let foundStore

    if (id) {
      Object.keys(stores).map((state) => {
        const stateStores = stores[state]

        stateStores.map((store) => {
          if (store.id === parseInt(id)) {
            foundStore = store
          }
        })
      })
    }

    return foundStore
  }

  /**
  * Render groups of options for all stores in every state.
  */
  renderChoices() {
    const { stores } = this.props

    return Object.keys(stores).map((state, idx) => {
      const stateStores = stores[state]

      return (
        <optgroup label={state} key={idx}>
          {stateStores.map(store => (
            <option key={store.id} value={store.id}>
              {store.display_name || store.name}{store.shipping_time ? ` (${store.shipping_time})` : ''}
            </option>
          ))}
        </optgroup>
      )
    })
  }

  render() {
    const {
      stores, defaultValue, namespace, isVisible, ...rest
    } = this.props
    const { activeStore } = this.state

    const address = activeStore ? (
      <div id={`${namespace}-address`}>
        <address>
          {activeStore.address2}<br />
          {activeStore.city}, {activeStore.state_abbr} {activeStore.zip}
        </address>
        <div>
          <Link href={`/retail_stores/${activeStore.id}`} target="_blank">Store Information</Link>
        </div>
      </div>
    ) : false

    return (
      <div>
        <SelectField
          name={`${namespace}[retail_store_id]`}
          label="Store Location"
          required
          defaultValue={defaultValue}
          {...rest}
          disabled={!isVisible}
          onChange={this.onChangeHandler}
        >
          <option value="">Select a Store</option>
          {this.renderChoices()}
        </SelectField>
        {address}
      </div>
    )
  }
}

ShipToStoreSelector.defaultProps = {
  stores: {},
  namespace: '',
  isVisible: true,
}

ShipToStoreSelector.propTypes = {
  // List of stores grouped by state, where key is the state name.
  stores: PropTypes.object,
  // Namespace for select field name.
  namespace: PropTypes.string,
  // If the field is initially visible, changes to this are currently handled elsewhere with jQuery.
  isVisible: PropTypes.bool,
}

export default ShipToStoreSelector
